import { useEffect, useState } from 'react';
import { api, type Brand, type Idea, type ModelInfo, type PlannedScene, type ScenePlan, type TalentRow } from '../lib/api';
import { LOOK_EVERY_MS, GIVE_UP_AFTER, GAVE_UP } from '../lib/waiting';
import { ModelPicker } from './ModelPicker';
import { ModelOptions } from './ModelOptions';
import { useBench } from './quick/useBench';

// The Scene Builder (199): a story, not a clip.
//
// The first two benches make one thing at a time, which is not how anybody
// works. A creator has a premise, and a premise is ten shots. Ten eight-second
// shots is eighty seconds — an advert or a short film — and that is the length
// at which generated video is affordable at all. A generated twelve-minute
// episode never will be.
//
// Two presses, deliberately. The first asks the director for a plan and buys
// nothing; the second commissions it. At roughly a thousand credits a shot the
// expensive mistake is not a bad shot, it is ten shots of a plan that was
// almost right — so the plan is shown, editable, with its warnings, before any
// money moves.

const MAX_SCENES = 12;

export function SceneBuilder({ brand, talent, onError, prefill, onUsedPrefill }: {
  brand: Brand | null; talent: TalentRow[]; onError: (m: string | null) => void;
  /** 200: the Idea Lab's proposal, read once and handed back. */
  prefill?: Idea | null;
  onUsedPrefill?: () => void;
}) {
  const [premise, setPremise] = useState('');
  const [name, setName] = useState('');
  const [cast, setCast] = useState<string[]>([]);
  const [count, setCount] = useState(10);
  const [plan, setPlan] = useState<PlannedScene[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [voiceless, setVoiceless] = useState<string[]>([]);
  const [busy, setBusy] = useState<'plan' | 'build' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [plans, setPlans] = useState<ScenePlan[]>([]);
  const [stalled, setStalled] = useState(false);
  // 204: the model the shots are made on, its finish, and the server's quote for the whole plan. No rate lives here.
  const [videoModels, setVideoModels] = useState<ModelInfo[] | null>(null);
  useEffect(() => { let live = true; api.models('video').then((m) => { if (live) setVideoModels(m); }).catch(() => undefined); return () => { live = false; }; }, []);

  // The proposal fills the form and then is let go: it is a starting point the
  // creator edits, never a command. Everyone with a ready portrait is cast,
  // because a scene refuses anybody without one anyway.
  useEffect(() => {
    if (!prefill || prefill.action !== 'plan_scenes') return;
    const premiseFrom = String(prefill.args.premise ?? '');
    if (premiseFrom) { setPremise(premiseFrom); setName(premiseFrom.slice(0, 60)); }
    const n = Number(prefill.args.count);
    if (Number.isFinite(n) && n >= 2) setCount(Math.min(MAX_SCENES, Math.trunc(n)));
    setCast(talent.filter((t) => t.portrait === 'ready').slice(0, 3).map((t) => t.id));
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, talent.length]);

  const chosen = cast.map((id) => talent.find((t) => t.id === id)).filter((t): t is TalentRow => Boolean(t));
  const faceless = chosen.filter((t) => t.portrait !== 'ready');
  const shots = plan?.length ?? count;
  const seconds = shots * 8;
  const bench = useBench('video', videoModels ?? undefined, () => ({ shots, seconds: 8 }), shots);
  const credits = bench.credits;

  async function loadPlans() {
    try { setPlans(await api.scenePlans(brand?.id ?? null)); } catch (e) { onError((e as Error).message); }
  }
  useEffect(() => { void loadPlans(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brand?.id]);

  // A shot is minutes, and ten run in parallel. Watched while any is building,
  // with a visible give-up rather than a spinner that lies.
  const building = plans.some((p) => Number(p.ready ?? 0) + Number(p.failed ?? 0) < Number(p.n ?? 0));
  useEffect(() => {
    if (!building) { setStalled(false); return; }
    let looks = 0;
    const t = setInterval(() => {
      if (looks++ >= GIVE_UP_AFTER) { clearInterval(t); setStalled(true); return; }
      void loadPlans();
    }, LOOK_EVERY_MS);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building]);

  const why = cast.length === 0 ? 'Pick who is in it'
    : faceless.length ? `${faceless.map((t) => t.name).join(', ')} ${faceless.length === 1 ? 'has' : 'have'} no portrait — a shot has nothing to hold their face to`
    : premise.trim().length < 10 ? 'Write the premise'
    : null;

  async function askDirector() {
    setBusy('plan'); onError(null); setNote(null);
    try {
      const r = await api.planScenes({ premise: premise.trim(), cast, scenes: count });
      setPlan(r.scenes); setWarnings(r.warnings); setVoiceless(r.voiceless);
      if (!name.trim()) setName(premise.trim().slice(0, 60));
    } catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  async function build() {
    if (!plan) return;
    setBusy('build'); onError(null);
    try {
      const byName = new Map(talent.map((t) => [t.name.toLowerCase(), t.id]));
      const r = await api.buildPlan({
        name: name.trim() || 'Untitled', premise: premise.trim(), brandId: brand?.id ?? null,
        ...(bench.model ? { model: bench.model.id } : {}), ...(bench.sel.resolution ? { resolution: bench.sel.resolution } : {}), ...(bench.sel.audio !== undefined ? { audio: bench.sel.audio } : {}),
        scenes: plan.map((s) => ({
          beat: s.beat, action: s.action,
          cast: s.cast.map((n) => byName.get(n.toLowerCase())).filter((x): x is string => Boolean(x)),
          speaker: s.speaker ? byName.get(s.speaker.toLowerCase()) ?? null : null,
          line: s.line,
        })),
      });
      setPlan(null); setWarnings([]);
      setNote(`${r.queued} ${r.queued === 1 ? 'shot' : 'shots'} commissioned. They generate in parallel — a few minutes.`);
      void loadPlans();
    } catch (e) { onError((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }} data-tool="scene-builder">
      {note && <p className="ok" style={{ margin: 0 }}>{note}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)', gap: 14 }}>
        {/* Who is in the story. Not per shot — the director casts each shot. */}
        <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
          <span className="sub-dim">Who is in the story</span>
          {talent.length === 0 && <p className="sub-dim" style={{ margin: 0, fontSize: 12 }}>Nobody in the vault yet. Add talent first — a shot is held together by their portrait.</p>}
          {talent.map((t) => (
            <button key={t.id} type="button" className={`btn tiny${cast.includes(t.id) ? ' pri' : ''}`}
                    style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => setCast((c) => (c.includes(t.id) ? c.filter((x) => x !== t.id) : [...c, t.id]))}>
              {t.name}
              <span className="sub-dim" style={{ marginLeft: 6, fontSize: 11 }}>
                {t.portrait !== 'ready' ? 'no portrait' : t.voice_id ? '' : 'no voice'}
              </span>
            </button>
          ))}
          <label className="sub-dim" style={{ fontSize: 12, marginTop: 6 }}>How many shots
            <select value={count} onChange={(e) => setCount(Number(e.target.value))} style={{ width: '100%' }}>
              {[4, 6, 8, 10, MAX_SCENES].map((n) => <option key={n} value={n}>{n} shots · {n * 8}s</option>)}
            </select>
          </label>
        </div>

        <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
          <ModelPicker bench="video" variant="card" models={videoModels} value={bench.model} onChange={bench.setModel} />
          <ModelOptions bench="video" model={bench.model} value={bench.sel} onChange={bench.setSel} />
          <textarea value={premise} rows={3} maxLength={2000}
                    placeholder="The premise — a day in the life of a squirrel family, from waking up to bedtime, taking in breakfast, play, work and dinner"
                    onChange={(e) => setPremise(e.target.value)} />
          <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
            <input value={name} maxLength={200} placeholder="Name it (optional)" onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
            <button type="button" className="btn" disabled={Boolean(why) || busy !== null} data-move="scene-quote" title={why ?? 'The director proposes the shots. Nothing is bought.'}
                    onClick={() => void askDirector()}>
              {busy === 'plan' ? 'Thinking…' : plan ? 'Plan it again' : 'Plan the shots'}
            </button>
          </div>
          <p className="sub-dim" style={{ margin: 0, fontSize: 12 }} data-tool="scene-quote">
            {why ?? (credits === null ? `Planning is free. Reading the price list for ${shots} shots…` : `Planning is free. Building ${shots} shots costs about ${credits.toLocaleString()} credits for ${seconds} seconds${bench.quote && !bench.quote.balance.ok ? ` — short by ${bench.quote.balance.shortBy.toLocaleString()}` : ''}.`)}
          </p>
        </div>
      </div>

      {/* The plan, before the money. Editable, because a shot is a dollar. */}
      {plan && (
        <div style={{ display: 'grid', gap: 8 }} data-tool="scene-plan">
          <div className="row wrap" style={{ gap: 8, alignItems: 'baseline' }}>
            <h4 style={{ margin: 0 }}>{plan.length} shots</h4>
            <span className="sub-dim" style={{ fontSize: 12 }}>{seconds} seconds · about {(credits ?? 0).toLocaleString()} credits</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn pri" disabled={busy !== null} onClick={() => void build()}>
              {busy === 'build' ? 'Commissioning…' : `Build ${plan.length} shots`}
            </button>
          </div>

          {warnings.length > 0 && (
            <div className="warn" style={{ display: 'grid', gap: 3, padding: 10, borderRadius: 6, border: '1px solid var(--line)' }} data-tool="scene-warnings">
              <strong style={{ fontSize: 13 }}>Worth fixing before you buy these</strong>
              {warnings.map((w, i) => <span key={i} className="sub-dim" style={{ fontSize: 12 }}>· {w}</span>)}
            </div>
          )}
          {voiceless.length > 0 && (
            <p className="sub-dim" style={{ margin: 0, fontSize: 12 }}>
              {voiceless.join(', ')} {voiceless.length === 1 ? 'has' : 'have'} no voice, so their lines cannot be read. Give them one on Talent.
            </p>
          )}

          {plan.map((s, i) => (
            <div key={s.slug} style={{ display: 'grid', gap: 4, padding: 10, border: '1px solid var(--line)', borderRadius: 6 }}>
              <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span className="sub-dim" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                <strong style={{ fontSize: 14 }}>{s.beat}</strong>
                <span className="sub-dim" style={{ fontSize: 12 }}>{s.cast.join(', ')}</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn tiny" title="Drop this shot" onClick={() => setPlan((p) => p!.filter((_, j) => j !== i))}>×</button>
              </div>
              <textarea value={s.action} rows={3} maxLength={2000} style={{ fontSize: 13 }}
                        onChange={(e) => setPlan((p) => p!.map((x, j) => (j === i ? { ...x, action: e.target.value } : x)))} />
              {s.line && <span className="sub-dim" style={{ fontSize: 12 }}>{s.speaker}: “{s.line}”</span>}
            </div>
          ))}
        </div>
      )}

      {/* What has been built. */}
      <div style={{ display: 'grid', gap: 6 }} data-tool="scene-plans">
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Stories built here</h4>
          <span className="sub-dim" style={{ fontSize: 12 }}>{plans.length ? `${plans.length} in the library under Scenes` : 'nothing yet'}</span>
          {building && !stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· shots are generating in parallel</span>}
          {stalled && <span className="sub-dim" style={{ fontSize: 12 }}>· {GAVE_UP} <button type="button" className="btn tiny" onClick={() => { setStalled(false); void loadPlans(); }}>Look again</button></span>}
        </div>
        {plans.slice(0, 10).map((p) => {
          const n = Number(p.n ?? 0), ready = Number(p.ready ?? 0), failed = Number(p.failed ?? 0);
          return (
            <div key={p.id} className="row" style={{ gap: 8, alignItems: 'center', padding: '5px 0', borderTop: '1px solid var(--line)' }}>
              <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.premise}>{p.name}</span>
              <span className="sub-dim" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                {ready} of {n} ready{failed ? `, ${failed} failed` : ''}
              </span>
              <button type="button" className="btn tiny" title="Take it off this list. The footage stays in the library."
                      onClick={() => { api.deleteScenePlan(p.id).then(loadPlans).catch((e) => onError((e as Error).message)); }}>×</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
